import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera, Upload, Loader2 } from 'lucide-react';
import { useT } from '../context/LanguageContext';

// Scanner voor QR/streepjescodes. Live camera + foto-upload fallback.
// Roept onResult(text) aan bij een succesvolle scan.
export default function PartScanner({ onResult, onClose }) {
  const { t } = useT();
  const elId = useRef(`scan-${Math.random().toString(36).slice(2)}`);
  const fileElId = useRef(`scanfile-${Math.random().toString(36).slice(2)}`);
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);
  const [scanningFile, setScanningFile] = useState(false);

  const stopLive = async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      if (scanner.getState && scanner.getState() === 2) await scanner.stop();
      await scanner.clear();
    } catch { /* noop */ }
  };

  useEffect(() => {
    let stopped = false;
    const scanner = new Html5Qrcode(elId.current, { verbose: false });
    scannerRef.current = scanner;

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 240, height: 240 } },
      (decodedText) => {
        if (stopped) return;
        stopped = true;
        stopLive().finally(() => onResult(decodedText));
      },
      () => { /* scan-miss, negeren */ }
    ).then(() => { if (!stopped) setReady(true); })
      .catch((e) => setError(e?.message || String(e)));

    return () => { stopped = true; stopLive(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // sta opnieuw selecteren van hetzelfde bestand toe
    if (!file) return;
    setError('');
    setScanningFile(true);
    await stopLive();
    const fs = new Html5Qrcode(fileElId.current, { verbose: false });
    try {
      const decodedText = await fs.scanFile(file, false);
      try { await fs.clear(); } catch { /* noop */ }
      onResult(decodedText);
    } catch (err) {
      try { await fs.clear(); } catch { /* noop */ }
      setError(t('Geen code gevonden in de foto. Kies een scherpe foto van de QR-/streepjescode.', 'Geen code gevonden in de foto. Kies een scherpe foto van de QR-/streepjescode.'));
      setScanningFile(false);
    }
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, width: '94vw' }} data-testid="part-scanner-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Camera size={18} /> {t('Scan onderdeel', 'Scan onderdeel')}</h3>
          <button className="cs-btn ghost" onClick={onClose} data-testid="ps-close"><X size={16} /></button>
        </div>

        {error ? (
          <div className="cs-empty" data-testid="ps-error" style={{ color: '#dc2626' }}>
            {error.startsWith(t('Geen code', 'Geen code')) ? error : `${t('Kan camera niet starten', 'Kan camera niet starten')}: ${error}`}
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>{t('Geen camera? Upload hieronder een foto van de code.', 'Geen camera? Upload hieronder een foto van de code.')}</div>
          </div>
        ) : (
          <>
            <div id={elId.current} data-testid="ps-video" style={{ width: '100%', borderRadius: 10, overflow: 'hidden', background: '#0f172a', minHeight: 240 }} />
            <p style={{ fontSize: 12.5, color: '#64748b', marginTop: 10, textAlign: 'center' }}>
              {ready ? t('Richt de camera op een QR- of streepjescode', 'Richt de camera op een QR- of streepjescode') : t('Camera starten…', 'Camera starten…')}
            </p>
          </>
        )}

        {/* Foto-upload fallback (werkt ook zonder camera) */}
        <div style={{ borderTop: '1px solid var(--cs-border)', marginTop: 12, paddingTop: 12 }}>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFile} style={{ display: 'none' }} data-testid="ps-file-input" />
          <button className="cs-btn" style={{ width: '100%', justifyContent: 'center' }} disabled={scanningFile}
            onClick={() => fileInputRef.current && fileInputRef.current.click()} data-testid="ps-upload-btn">
            {scanningFile ? <Loader2 size={16} className="spin" /> : <Upload size={16} />} {t('Upload foto van code', 'Upload foto van code')}
          </button>
          <p style={{ fontSize: 11.5, color: '#94a3b8', margin: '8px 0 0', textAlign: 'center' }}>
            {t('Geen webcam? Maak/kies een foto van de QR- of streepjescode.', 'Geen webcam? Maak/kies een foto van de QR- of streepjescode.')}
          </p>
        </div>

        <div id={fileElId.current} style={{ display: 'none' }} />
      </div>
    </div>
  );
}
